// This is the file: ../../utils/ai_assistant.js

// All AI calls route through the JobEng /api/ai proxy.
// Proxy handles Anthropic → Groq → OpenRouter → Gemini fallback automatically.
const JOBENG_API_BASE = 'https://api.jobeng.ai';
const JOBENG_API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Kept for any backward-compat references — both point to the proxy.
const OPENAI_API_KEY   = '';
const DEEPSEEK_API_KEY = '';
const OPENAI_API_URL   = `${JOBENG_API_BASE}/api/ai`;
const DEEPSEEK_API_URL = `${JOBENG_API_BASE}/api/ai`;
const EVENTBRITE_API_URL = 'https://www.eventbriteapi.com/v3';

// Common error messages for better maintainability
const ERROR_MESSAGES = {
  NETWORK: "I'm having trouble connecting. Please check your internet connection and try again.",
  GENERAL: "I'm sorry, I encountered an error while processing your request. Please try again later.",
  EMPTY_RESPONSE: "The AI returned an empty response. Please try again with a different prompt.",
  API_ERROR: "The AI service is temporarily unavailable. Please try again in a moment.",
  AUTH_ERROR: "Authentication failed. Please check your API key configuration.",
  NO_PROVIDER: "No AI provider available. Please check your API key configuration.",
  EVENTBRITE_ERROR: "Unable to fetch events at this time. Please try again later."
};

// Default model configuration
const DEFAULT_MODELS = {
  openai: 'gpt-4o-mini',
  deepseek: 'deepseek-chat'
};
const DEFAULT_TEMPERATURE = 0.7;

// Available AI providers in order of preference
const AI_PROVIDERS = ['openai', 'deepseek'];

/**
 * Selects the best available AI provider based on available API keys
 * @returns {string} The selected provider name
 */
function getAvailableProvider() {
  if (OPENAI_API_KEY && OPENAI_API_KEY !== 'your_openai_api_key_here') {
    return 'openai';
  } else if (DEEPSEEK_API_KEY && DEEPSEEK_API_KEY !== 'your_deepseek_api_key_here') {
    return 'deepseek';
  }
  return null;
}

/**
 * Gets the appropriate API URL and headers for the selected provider
 * @param {string} provider The AI provider name
 * @returns {Object} API configuration {url, headers}
 */
function getApiConfig(provider) {
  switch (provider) {
    case 'openai':
      return {
        url: OPENAI_API_URL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        }
      };
    case 'deepseek':
      return {
        url: DEEPSEEK_API_URL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        }
      };
    default:
      throw new Error(ERROR_MESSAGES.NO_PROVIDER);
  }
}

/**
 * Gets the appropriate model name for the provider
 * @param {string} provider The AI provider name
 * @param {string} preferredModel Preferred model (optional)
 * @returns {string} The model name to use
 */
function getModelForProvider(provider, preferredModel = null) {
  if (preferredModel) return preferredModel;
  return DEFAULT_MODELS[provider] || DEFAULT_MODELS.openai;
}

/**
 * Makes a request to the AI API with common error handling
 * @param {Object} requestBody The request body for the AI API
 * @param {string} provider The AI provider to use
 * @returns {Promise<Object>} The API response data
 */
async function makeAIRequest(requestBody, provider = null) {
  // Routes to the JobEng /api/ai proxy (Anthropic → Groq → OpenRouter → Gemini).
  // Accepts the same { messages, temperature, max_tokens } shape as before
  // and returns the same OpenAI-compatible response shape.
  try {
    console.log('Making request to JobEng AI proxy...');
    const response = await fetch(`${JOBENG_API_BASE}/api/ai`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': JOBENG_API_KEY,
      },
      body: JSON.stringify({
        messages:    requestBody.messages,
        max_tokens:  requestBody.max_tokens,
        temperature: requestBody.temperature,
        model:       requestBody.model,
        provider,    // lets caller request a specific provider optionally
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('JobEng AI proxy error:', errorData);
      if (response.status === 401) throw new Error(ERROR_MESSAGES.AUTH_ERROR);
      if (response.status === 429) throw new Error('Rate limit exceeded. Please try again in a moment.');
      if (response.status >= 500) throw new Error('AI service server error. Please try again later.');
      throw new Error(errorData.error || `AI proxy request failed with status ${response.status}`);
    }

    const data = await response.json();
    // Proxy returns { success, text, choices:[{message:{content}}], model, provider }
    // — already OpenAI-compatible, so all callers work without changes.
    return data;
  } catch (error) {
    console.error('AI API Request Error:', error);
    throw error;
  }
}

/**
 * Makes a request to the Eventbrite API
 * @param {string} endpoint The Eventbrite API endpoint
 * @param {Object} params Query parameters
 * @returns {Promise<Object>} The API response data
 */
async function makeEventbriteRequest(endpoint, params = {}) {
  try {
    if (!EVENTBRITE_API_KEY || EVENTBRITE_API_KEY === 'your_eventbrite_api_key_here') {
      throw new Error('Eventbrite API key is not configured. Please check your .env file.');
    }

    // Build URL with proper authentication
    const queryParams = new URLSearchParams(params);
    const url = `${EVENTBRITE_API_URL}${endpoint}?${queryParams}`;

    console.log(`Making Eventbrite request to: ${url.substring(0, 100)}...`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${EVENTBRITE_API_KEY}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Eventbrite API Error Response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error_description: errorText };
      }
      
      if (response.status === 401) {
        throw new Error('Eventbrite authentication failed - check your API key');
      } else if (response.status === 403) {
        throw new Error('Eventbrite access forbidden - check API key permissions');
      } else if (response.status === 429) {
        throw new Error('Eventbrite rate limit exceeded');
      } else {
        throw new Error(errorData.error_description || `Eventbrite API error: ${response.status}`);
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Eventbrite API Request Error:', error);
    throw error;
  }
}

/**
 * Handles common errors and returns appropriate user messages
 * @param {Error} error The caught error
 * @returns {string} User-friendly error message
 */
function handleError(error) {
  if (error.message.includes('Network request failed') || 
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network Error')) {
    console.error('>>> CRITICAL: Network error - check internet connection and Android permissions');
    return ERROR_MESSAGES.NETWORK;
  } else if (error.message.includes('API request failed') || 
             error.message.includes('Rate limit') ||
             error.message.includes('server error')) {
    return error.message;
  } else if (error.message.includes('Authentication failed') ||
             error.message.includes('API key') ||
             error.message.includes('No AI provider') ||
             error.message.includes('not configured')) {
    return ERROR_MESSAGES.AUTH_ERROR;
  } else if (error.message.includes('Eventbrite')) {
    return ERROR_MESSAGES.EVENTBRITE_ERROR;
  } else {
    return ERROR_MESSAGES.GENERAL;
  }
}

/**
 * Validates prompt input and trims excessive length
 * @param {string} prompt The user prompt to validate
 * @param {number} maxLength Maximum allowed length
 * @returns {string} Validated and trimmed prompt
 */
function validatePrompt(prompt, maxLength = 4000) {
  if (!prompt || typeof prompt !== 'string') {
    throw new Error('Prompt must be a non-empty string');
  }
  
  const trimmedPrompt = prompt.trim();
  if (trimmedPrompt.length === 0) {
    throw new Error('Prompt cannot be empty');
  }
  
  if (trimmedPrompt.length > maxLength) {
    console.warn(`Prompt truncated from ${trimmedPrompt.length} to ${maxLength} characters`);
    return trimmedPrompt.substring(0, maxLength);
  }
  
  return trimmedPrompt;
}

/**
 * Checks if Eventbrite API is properly configured
 * @returns {Object} Configuration status
 */
export function checkEventbriteConfiguration() {
  const hasApiKey = EVENTBRITE_API_KEY && EVENTBRITE_API_KEY !== 'your_eventbrite_api_key_here';
  
  return {
    hasApiKey,
    apiKeyLength: EVENTBRITE_API_KEY ? EVENTBRITE_API_KEY.length : 0,
    isConfigured: hasApiKey,
    message: hasApiKey ? 
      'Eventbrite API key is configured' : 
      'Eventbrite API key is not configured. Please check your .env file.'
  };
}

// AI FUNCTIONS

/**
 * Generates a career advice response using AI API.
 * @param {string} prompt The user's question and context.
 * @param {string} provider Specific provider to use (optional)
 * @returns {Promise<string>} The AI's response text.
 */
export async function getAICareerAdvice(prompt, provider = null) {
  try {
    const validatedPrompt = validatePrompt(prompt);
    
    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: "You are an expert AI Career Assistant. Provide concise, professional, and actionable advice. Keep response under 100 words. Focus on practical steps the user can take immediately.",
        },
        {
          role: 'user',
          content: validatedPrompt,
        },
      ],
      temperature: DEFAULT_TEMPERATURE,
      max_tokens: 256,
    }, provider);

    const advice = data.choices[0]?.message?.content;

    if (!advice) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    return advice.trim();

  } catch (error) {
    console.error('Error in getAICareerAdvice:', error);
    return handleError(error);
  }
}

/**
 * Generates marketing content using AI API.
 * @param {string} prompt The user's content request
 * @param {string} contentType Type of content (blog, social, email, ad)
 * @param {string} tone Writing tone (professional, conversational, etc.)
 * @param {string} provider Specific provider to use (optional)
 * @returns {Promise<string>} The AI's generated content
 */
export async function generateAIContent(prompt, contentType = 'blog', tone = 'professional', provider = null) {
  try {
    const validatedPrompt = validatePrompt(prompt);
    
    const systemMessage = `You are an expert marketing content creator specializing in ${contentType} content. 
Write in a ${tone} tone. Create engaging, professional content that resonates with the target audience.
Keep the content well-structured and actionable. Format your response appropriately for ${contentType}.`;

    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: systemMessage,
        },
        {
          role: 'user',
          content: validatedPrompt,
        },
      ],
      temperature: DEFAULT_TEMPERATURE,
      max_tokens: 1500,
    }, provider);

    const content = data.choices[0]?.message?.content;

    if (!content) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    return content.trim();

  } catch (error) {
    console.error('Error in generateAIContent:', error);
    return handleError(error);
  }
}

/**
 * Generates a detailed logo concept prompt using AI API.
 * @param {string} prompt The detailed prompt for the logo concept.
 * @param {string} provider Specific provider to use (optional)
 * @returns {Promise<string>} The AI's generated logo concept text.
 */
export async function generateLogoConcept(prompt, provider = null) {
  try {
    const validatedPrompt = validatePrompt(prompt);
    
    const systemMessage = `You are an expert logo designer AI. Generate a single, concise, and highly descriptive paragraph suitable for text-to-image models like DALL-E or Midjourney. 
Focus on:
- Visual elements and symbolism
- Composition and layout
- Color palette and mood
- Style and aesthetics
Provide only the prompt itself without any introductory or concluding remarks.`;

    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: systemMessage,
        },
        {
          role: 'user',
          content: validatedPrompt,
        },
      ],
      temperature: 0.8, // Slightly higher temperature for creativity
      max_tokens: 500,
    }, provider);

    const concept = data.choices[0]?.message?.content;

    if (!concept) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    return concept.trim();

  } catch (error) {
    console.error('Error in generateLogoConcept:', error);
    return handleError(error);
  }
}

/**
 * Generates business intelligence insights using AI API.
 * @param {Object|string} businessData The business data and metrics
 * @param {string} analysisType Type of analysis (insights, predictions, recommendations)
 * @param {string} provider Specific provider to use (optional)
 * @returns {Promise<string>} The AI's generated business insights
 */
export async function generateBusinessInsights(businessData, analysisType = 'insights', provider = null) {
  try {
    const dataString = typeof businessData === 'string' ? businessData : JSON.stringify(businessData);
    const validatedPrompt = validatePrompt(dataString);
    
    const systemMessage = `You are an expert business intelligence analyst. Analyze the provided business data and generate ${analysisType} that are:
- Data-driven and evidence-based
- Actionable and practical
- Focused on growth opportunities
- Clear and concise
- Tailored for construction and engineering businesses

Provide insights in a structured format with clear recommendations. Use bullet points or numbered lists for better readability.`;

    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: systemMessage,
        },
        {
          role: 'user',
          content: `Business Data: ${validatedPrompt}\n\nAnalysis Type: ${analysisType}`,
        },
      ],
      temperature: DEFAULT_TEMPERATURE,
      max_tokens: 1000,
    }, provider);

    const insights = data.choices[0]?.message?.content;

    if (!insights) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    return insights.trim();

  } catch (error) {
    console.error('Error in generateBusinessInsights:', error);
    return handleError(error);
  }
}

/**
 * Generates multiple response options for user choice
 * @param {string} prompt The user's prompt
 * @param {number} optionsCount Number of options to generate (default: 3)
 * @param {string} provider Specific provider to use (optional)
 * @returns {Promise<string[]>} Array of response options
 */
export async function generateResponseOptions(prompt, optionsCount = 3, provider = null) {
  try {
    const validatedPrompt = validatePrompt(prompt);
    
    const systemMessage = `Generate exactly ${optionsCount} distinct variations in response to the user's query. 
Each variation should approach the topic from a different angle while maintaining accuracy and relevance.
Number each option clearly (1, 2, 3, etc.). Make each option complete and self-contained.`;

    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: systemMessage,
        },
        {
          role: 'user',
          content: validatedPrompt,
        },
      ],
      temperature: 0.8, // Higher temperature for more variety
      max_tokens: 800,
    }, provider);

    const response = data.choices[0]?.message?.content;
    
    if (!response) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    // Split the response into individual options
    const options = response.split(/\d+\.\s*/).filter(opt => opt.trim().length > 0);
    
    return options.length > 0 ? options.slice(0, optionsCount) : [response.trim()];

  } catch (error) {
    console.error('Error in generateResponseOptions:', error);
    return [handleError(error)];
  }
}

/**
 * Summarizes long text content
 * @param {string} text The text to summarize
 * @param {number} maxLength Maximum length of summary
 * @param {string} provider Specific provider to use (optional)
 * @returns {Promise<string>} The summarized text
 */
export async function summarizeText(text, maxLength = 500, provider = null) {
  try {
    const validatedPrompt = validatePrompt(text, 10000); // Allow longer input for summarization
    
    const systemMessage = `You are an expert at summarizing content. Create a concise summary that captures the key points and main ideas. Keep the summary under ${maxLength} characters while maintaining all essential information.`;

    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: systemMessage,
        },
        {
          role: 'user',
          content: `Please summarize the following text:\n\n${validatedPrompt}`,
        },
      ],
      temperature: 0.3, // Lower temperature for more consistent summaries
      max_tokens: Math.ceil(maxLength * 1.1), // Allow slight overflow
    }, provider);

    const summary = data.choices[0]?.message?.content;

    if (!summary) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    return summary.trim();

  } catch (error) {
    console.error('Error in summarizeText:', error);
    return handleError(error);
  }
}

// EVENTBRITE API FUNCTIONS

/**
 * Searches for events on Eventbrite
 * @param {Object} searchParams Search parameters
 * @param {string} searchParams.query Search query keywords
 * @param {string} searchParams.location Location (city, state, or zip code)
 * @param {string} searchParams.within Distance in miles (e.g., "10mi")
 * @param {string} searchParams.sortBy Sort by "date", "distance", "best", "venue_distance"
 * @param {string} searchParams.categories Comma-separated category IDs
 * @param {string} searchParams.startDate Start date (YYYY-MM-DD)
 * @param {string} searchParams.endDate End date (YYYY-MM-DD)
 * @param {number} searchParams.pageSize Number of results per page (1-1000)
 * @returns {Promise<Object>} Eventbrite search results
 */
export async function searchEvents(searchParams = {}) {
  try {
    const {
      query = '',
      location = '',
      within = '25mi',
      sortBy = 'date',
      categories = '',
      startDate = '',
      endDate = '',
      pageSize = 20
    } = searchParams;

    const params = {
      'q': query,
      'location.address': location,
      'location.within': within,
      'sort_by': sortBy,
      'categories': categories,
      'start_date.range_start': startDate,
      'start_date.range_end': endDate,
      'page_size': Math.min(pageSize, 1000)
    };

    // Remove empty parameters
    Object.keys(params).forEach(key => {
      if (!params[key]) {
        delete params[key];
      }
    });

    const data = await makeEventbriteRequest('/events/search/', params);
    return data;

  } catch (error) {
    console.error('Error in searchEvents:', error);
    throw error;
  }
}

/**
 * Enhanced engineering events search with multiple query attempts
 * @param {string} location Location for event search
 * @param {string} engineeringField Specific engineering field (optional)
 * @returns {Promise<Object>} Search results with events
 */
export async function searchEngineeringEvents(location, engineeringField = '') {
  try {
    // Common engineering-related search terms that work well on Eventbrite
    const engineeringQueries = [
      'tech talk',
      'programming workshop',
      'software development',
      'coding bootcamp',
      'AI conference',
      'machine learning',
      'data science',
      'web development',
      'mobile app development',
      'cloud computing',
      'devops',
      'cybersecurity',
      'iot internet of things',
      'robotics',
      '3d printing',
      'arduino raspberry pi',
      'tech meetup',
      'innovation',
      'startup pitch',
      'hackathon'
    ];

    // Add field-specific queries if provided
    if (engineeringField) {
      engineeringQueries.unshift(
        `${engineeringField} workshop`,
        `${engineeringField} conference`,
        `${engineeringField} meetup`
      );
    }

    let allEvents = [];
    let successfulQueries = [];

    // Try multiple queries to get better results
    for (const query of engineeringQueries.slice(0, 5)) { // Limit to 5 queries to avoid rate limiting
      try {
        console.log(`Trying engineering event search with: "${query}"`);
        const results = await searchEvents({
          query: query,
          location: location,
          pageSize: 10,
          sortBy: 'date'
        });

        if (results.events && results.events.length > 0) {
          allEvents = [...allEvents, ...results.events];
          successfulQueries.push(query);
          console.log(`Found ${results.events.length} events for "${query}"`);
        }
      } catch (queryError) {
        console.warn(`Query "${query}" failed:`, queryError.message);
        // Continue with next query
      }
    }

    // Remove duplicate events
    const uniqueEvents = allEvents.filter((event, index, self) =>
      index === self.findIndex(e => e.id === event.id)
    );

    return {
      events: uniqueEvents,
      successfulQueries: successfulQueries,
      totalFound: uniqueEvents.length,
      searchLocation: location
    };

  } catch (error) {
    console.error('Error in searchEngineeringEvents:', error);
    throw error;
  }
}

/**
 * Gets sample engineering events when no real events are found
 * @param {string} location Location for the sample events
 * @returns {Array} Sample engineering events
 */
function getSampleEngineeringEvents(location) {
  const currentDate = new Date();
  const futureDate1 = new Date(currentDate);
  futureDate1.setDate(currentDate.getDate() + 7);
  
  const futureDate2 = new Date(currentDate);
  futureDate2.setDate(currentDate.getDate() + 14);
  
  const futureDate3 = new Date(currentDate);
  futureDate3.setDate(currentDate.getDate() + 21);

  return [
    {
      id: 'sample_eng_1',
      name: {
        text: 'Tech Innovation & Engineering Meetup'
      },
      description: {
        text: 'Monthly gathering of engineers, developers, and tech enthusiasts. Network with professionals and discuss the latest trends in software engineering, AI, and emerging technologies.'
      },
      url: 'https://example.com/tech-meetup',
      start: {
        local: futureDate1.toISOString(),
        utc: futureDate1.toISOString()
      },
      end: {
        local: new Date(futureDate1.getTime() + 3 * 60 * 60 * 1000).toISOString(),
        utc: new Date(futureDate1.getTime() + 3 * 60 * 60 * 1000).toISOString()
      },
      venue: {
        id: 'sample_venue_1',
        name: 'Tech Hub ' + location,
        address: {
          address_1: '123 Innovation Drive'
        }
      },
      logo: {
        url: null
      },
      is_free: true,
      ticket_availability: {
        has_available_tickets: true,
        is_sold_out: false,
        minimum_ticket_price: {
          display: 'Free',
          value: 0
        },
        maximum_ticket_price: {
          display: 'Free',
          value: 0
        }
      },
      category_id: '110',
      is_sample: true
    },
    {
      id: 'sample_eng_2',
      name: {
        text: 'Software Development Workshop: Modern Web Technologies'
      },
      description: {
        text: 'Hands-on workshop covering React, Node.js, and cloud deployment. Perfect for engineers looking to update their skills or learn full-stack development.'
      },
      url: 'https://example.com/web-workshop',
      start: {
        local: futureDate2.toISOString(),
        utc: futureDate2.toISOString()
      },
      end: {
        local: new Date(futureDate2.getTime() + 4 * 60 * 60 * 1000).toISOString(),
        utc: new Date(futureDate2.getTime() + 4 * 60 * 60 * 1000).toISOString()
      },
      venue: {
        id: 'sample_venue_2',
        name: 'Code Academy ' + location,
        address: {
          address_1: '456 Developer Street'
        }
      },
      logo: {
        url: null
      },
      is_free: false,
      ticket_availability: {
        has_available_tickets: true,
        is_sold_out: false,
        minimum_ticket_price: {
          display: '$25.00',
          value: 2500
        },
        maximum_ticket_price: {
          display: '$25.00',
          value: 2500
        }
      },
      category_id: '102',
      is_sample: true
    },
    {
      id: 'sample_eng_3',
      name: {
        text: 'AI & Machine Learning Conference 2024'
      },
      description: {
        text: 'Annual conference featuring talks from industry leaders in artificial intelligence, machine learning, and data engineering. Includes workshops and networking sessions.'
      },
      url: 'https://example.com/ai-conference',
      start: {
        local: futureDate3.toISOString(),
        utc: futureDate3.toISOString()
      },
      end: {
        local: new Date(futureDate3.getTime() + 8 * 60 * 60 * 1000).toISOString(),
        utc: new Date(futureDate3.getTime() + 8 * 60 * 60 * 1000).toISOString()
      },
      venue: {
        id: 'sample_venue_3',
        name: 'Convention Center ' + location,
        address: {
          address_1: '789 Tech Boulevard'
        }
      },
      logo: {
        url: null
      },
      is_free: false,
      ticket_availability: {
        has_available_tickets: true,
        is_sold_out: false,
        minimum_ticket_price: {
          display: '$75.00',
          value: 7500
        },
        maximum_ticket_price: {
          display: '$150.00',
          value: 15000
        }
      },
      category_id: '105',
      is_sample: true
    }
  ];
}

/**
 * Smart engineering event search with fallback to sample events
 * @param {string} location Location for event search
 * @param {string} engineeringField Specific engineering field
 * @param {boolean} allowSampleEvents Whether to return sample events if none found
 * @returns {Promise<Object>} Events and search metadata
 */
export async function getEngineeringEvents(location, engineeringField = '', allowSampleEvents = true) {
  try {
    if (!location) {
      throw new Error('Location is required for engineering events search');
    }

    console.log(`Searching for engineering events in: ${location}`);
    
    // First try the enhanced engineering search
    const searchResults = await searchEngineeringEvents(location, engineeringField);
    
    if (searchResults.events.length > 0) {
      return {
        events: searchResults.events.map(event => formatEventForDisplay(event)),
        totalEvents: searchResults.events.length,
        successfulQueries: searchResults.successfulQueries,
        hasRealEvents: true,
        location: location,
        message: `Found ${searchResults.events.length} engineering-related events in ${location}`
      };
    }

    // If no real events found and sample events are allowed
    if (allowSampleEvents) {
      console.log('No real engineering events found, returning sample events');
      const sampleEvents = getSampleEngineeringEvents(location);
      
      return {
        events: sampleEvents.map(event => formatEventForDisplay(event)),
        totalEvents: sampleEvents.length,
        successfulQueries: ['sample events'],
        hasRealEvents: false,
        location: location,
        message: `No current engineering events found in ${location}. Here are some sample events that are typically available. Check Eventbrite regularly for updated listings.`,
        isSampleData: true
      };
    }

    // No events found and samples not allowed
    return {
      events: [],
      totalEvents: 0,
      successfulQueries: [],
      hasRealEvents: false,
      location: location,
      message: `No engineering events found in ${location}. Try expanding your search area or checking different dates.`
    };

  } catch (error) {
    console.error('Error in getEngineeringEvents:', error);
    
    // Provide sample events even on error if allowed
    if (allowSampleEvents) {
      const sampleEvents = getSampleEngineeringEvents(location);
      return {
        events: sampleEvents.map(event => formatEventForDisplay(event)),
        totalEvents: sampleEvents.length,
        successfulQueries: ['sample events - error fallback'],
        hasRealEvents: false,
        location: location,
        message: `Unable to search events at the moment. Here are sample engineering events for ${location}.`,
        isSampleData: true,
        error: error.message
      };
    }
    
    throw error;
  }
}

/**
 * Gets event categories from Eventbrite
 * @returns {Promise<Array>} List of event categories
 */
export async function getEventCategories() {
  try {
    const data = await makeEventbriteRequest('/categories/');
    return data.categories || [];
  } catch (error) {
    console.error('Error in getEventCategories:', error);
    throw error;
  }
}

/**
 * Gets event details by ID
 * @param {string} eventId Eventbrite event ID
 * @returns {Promise<Object>} Event details
 */
export async function getEventDetails(eventId) {
  try {
    if (!eventId) {
      throw new Error('Event ID is required');
    }

    const data = await makeEventbriteRequest(`/events/${eventId}/`);
    return data;
  } catch (error) {
    console.error('Error in getEventDetails:', error);
    throw error;
  }
}

/**
 * Gets events by organizer
 * @param {string} organizerId Eventbrite organizer ID
 * @param {number} pageSize Number of events to return
 * @returns {Promise<Object>} Organizer's events
 */
export async function getOrganizerEvents(organizerId, pageSize = 20) {
  try {
    if (!organizerId) {
      throw new Error('Organizer ID is required');
    }

    const data = await makeEventbriteRequest(`/organizers/${organizerId}/events/`, {
      'page_size': pageSize
    });
    return data;
  } catch (error) {
    console.error('Error in getOrganizerEvents:', error);
    throw error;
  }
}

/**
 * Gets popular events in a location
 * @param {string} location Location (city, state, or zip code)
 * @param {number} limit Number of events to return
 * @returns {Promise<Array>} Popular events
 */
export async function getPopularEvents(location, limit = 10) {
  try {
    const searchParams = {
      location: location,
      sortBy: 'best',
      pageSize: limit
    };

    const data = await searchEvents(searchParams);
    return data.events || [];
  } catch (error) {
    console.error('Error in getPopularEvents:', error);
    throw error;
  }
}

/**
 * Gets events by category
 * @param {string} categoryId Eventbrite category ID
 * @param {string} location Location for filtering
 * @param {number} limit Number of events to return
 * @returns {Promise<Array>} Events in the category
 */
export async function getEventsByCategory(categoryId, location = '', limit = 20) {
  try {
    const searchParams = {
      categories: categoryId,
      location: location,
      pageSize: limit
    };

    const data = await searchEvents(searchParams);
    return data.events || [];
  } catch (error) {
    console.error('Error in getEventsByCategory:', error);
    throw error;
  }
}

/**
 * Uses AI to generate event recommendations based on user preferences
 * @param {Object} userPreferences User preferences for events
 * @param {string} userPreferences.interests User interests
 * @param {string} userPreferences.location Preferred location
 * @param {string} userPreferences.dateRange Preferred date range
 * @param {string} userPreferences.budget Budget range
 * @param {string} provider AI provider to use
 * @returns {Promise<Object>} AI-generated event recommendations and search strategy
 */
export async function getAIEventRecommendations(userPreferences, provider = null) {
  try {
    const { interests, location, dateRange, budget } = userPreferences;
    
    const systemMessage = `You are an expert event recommendation assistant. Analyze the user's preferences and suggest the best types of events and search strategies for Eventbrite. 
Provide specific event categories, keywords, and search parameters that would work well. Be practical and specific.`;

    const userMessage = `User Preferences:
- Interests: ${interests}
- Location: ${location}
- Date Range: ${dateRange}
- Budget: ${budget}

Please suggest:
1. Specific event types/categories to search for
2. Recommended keywords for Eventbrite search
3. Any specific date ranges or filters to apply
4. Additional search tips`;

    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: systemMessage,
        },
        {
          role: 'user',
          content: userMessage,
        },
      ],
      temperature: 0.7,
      max_tokens: 500,
    }, provider);

    const recommendations = data.choices[0]?.message?.content;

    if (!recommendations) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    // Also perform an actual Eventbrite search based on the preferences
    let events = [];
    try {
      const eventbriteResults = await searchEvents({
        query: interests,
        location: location,
        pageSize: 10
      });
      events = eventbriteResults.events || [];
    } catch (eventbriteError) {
      console.warn('Eventbrite search failed, returning AI recommendations only:', eventbriteError);
    }

    return {
      aiRecommendations: recommendations.trim(),
      events: events,
      searchParams: {
        query: interests,
        location: location
      }
    };

  } catch (error) {
    console.error('Error in getAIEventRecommendations:', error);
    throw error;
  }
}

/**
 * Uses AI to generate better engineering event search strategies
 * @param {string} engineeringInterest Specific engineering interest
 * @param {string} location Location for events
 * @param {string} provider AI provider to use
 * @returns {Promise<Object>} AI-optimized search strategy
 */
export async function getAIEngineeringEventStrategy(engineeringInterest, location, provider = null) {
  try {
    const systemMessage = `You are an expert at finding engineering and tech events. Given a specific engineering interest and location, suggest the best search terms and strategies for Eventbrite.

Provide:
1. 3-5 specific search queries that would work well
2. Recommended Eventbrite categories
3. Tips for finding relevant events
4. Alternative event types that might be available

Be very practical and specific with search terms.`;

    const userMessage = `Engineering Interest: ${engineeringInterest}
Location: ${location}

What are the best Eventbrite search strategies for this combination?`;

    const data = await makeAIRequest({
      messages: [
        {
          role: 'system',
          content: systemMessage,
        },
        {
          role: 'user',
          content: userMessage,
        },
      ],
      temperature: 0.7,
      max_tokens: 600,
    }, provider);

    const strategy = data.choices[0]?.message?.content;

    if (!strategy) {
      throw new Error(ERROR_MESSAGES.EMPTY_RESPONSE);
    }

    // Also perform actual event search
    let actualEvents = [];
    try {
      const eventResults = await getEngineeringEvents(location, engineeringInterest, true);
      actualEvents = eventResults.events || [];
    } catch (searchError) {
      console.warn('Event search failed during strategy generation:', searchError);
    }

    return {
      aiStrategy: strategy.trim(),
      recommendedSearches: [
        `${engineeringInterest} workshop ${location}`,
        `tech ${engineeringInterest} meetup`,
        `${engineeringInterest} conference`,
        `programming ${engineeringInterest}`,
        `software engineering ${location}`
      ],
      actualEvents: actualEvents,
      searchPerformed: actualEvents.length > 0
    };

  } catch (error) {
    console.error('Error in getAIEngineeringEventStrategy:', error);
    throw error;
  }
}

/**
 * Formats event data for display in the app
 * @param {Object} event Raw event data from Eventbrite
 * @returns {Object} Formatted event data
 */
export function formatEventForDisplay(event) {
  if (!event) return null;

  const formattedEvent = {
    id: event.id,
    name: event.name?.text || 'Untitled Event',
    description: event.description?.text || '',
    url: event.url,
    start: event.start ? {
      local: event.start.local,
      utc: event.start.utc,
      formatted: new Date(event.start.local).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } : null,
    end: event.end ? {
      local: event.end.local,
      utc: event.end.utc,
      formatted: new Date(event.end.local).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } : null,
    venue: event.venue ? {
      id: event.venue.id,
      name: event.venue.name,
      address: event.venue.address
    } : null,
    logo: event.logo ? event.logo.url : null,
    is_free: event.is_free || false,
    ticket_availability: event.ticket_availability ? {
      has_available_tickets: event.ticket_availability.has_available_tickets,
      is_sold_out: event.ticket_availability.is_sold_out,
      minimum_ticket_price: event.ticket_availability.minimum_ticket_price,
      maximum_ticket_price: event.ticket_availability.maximum_ticket_price
    } : null,
    category: event.category_id ? {
      id: event.category_id,
      name: event.category_id
    } : null
  };

  // Add sample event flag if present
  if (event.is_sample) {
    formattedEvent.is_sample = true;
    formattedEvent.is_real_event = false;
  } else {
    formattedEvent.is_real_event = true;
  }

  return formattedEvent;
}

// UTILITY FUNCTIONS

/**
 * Utility function to check if the AI service is available
 * @param {string} provider Specific provider to check (optional)
 * @returns {Promise<boolean>} True if service is available
 */
export async function checkAIServiceStatus(provider = null) {
  try {
    const selectedProvider = provider || getAvailableProvider();
    
    if (!selectedProvider) {
      return false;
    }

    // Simple test request to check API connectivity
    await makeAIRequest({
      messages: [
        {
          role: 'user',
          content: 'Hello',
        },
      ],
      max_tokens: 5,
    }, selectedProvider);
    
    return true;
  } catch (error) {
    console.error('AI Service status check failed:', error);
    return false;
  }
}

/**
 * Checks if Eventbrite API is configured and available
 * @returns {Promise<boolean>} True if Eventbrite API is available
 */
export async function checkEventbriteStatus() {
  try {
    // Test the categories endpoint which is lightweight
    await makeEventbriteRequest('/categories/', { page_size: 1 });
    return true;
  } catch (error) {
    console.error('Eventbrite status check failed:', error);
    return false;
  }
}

/**
 * Gets information about available AI providers
 * @returns {Object} Information about available providers
 */
export function getAvailableProviders() {
  const providers = {};
  
  if (OPENAI_API_KEY && OPENAI_API_KEY !== 'your_openai_api_key_here') {
    providers.openai = {
      name: 'OpenAI',
      model: DEFAULT_MODELS.openai,
      available: true
    };
  }
  
  if (DEEPSEEK_API_KEY && DEEPSEEK_API_KEY !== 'your_deepseek_api_key_here') {
    providers.deepseek = {
      name: 'DeepSeek',
      model: DEFAULT_MODELS.deepseek,
      available: true
    };
  }
  
  return providers;
}

// Export constants for external use
export const AI_CONFIG = {
  DEFAULT_MODELS,
  DEFAULT_TEMPERATURE,
  MAX_PROMPT_LENGTH: 4000,
  ERROR_MESSAGES,
  AI_PROVIDERS
};

export const EVENTBRITE_CONFIG = {
  API_URL: EVENTBRITE_API_URL,
  MAX_PAGE_SIZE: 1000
};
